import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  UserPlus, Trash2, ShieldCheck, Ban, RefreshCw, Check, KeyRound, Crown, Gavel, Users2, ShieldCheck as ShieldIcon,
} from "lucide-react";

const ROLE_META = {
  super_admin:  { label: "Super Admin",  icon: Crown,      color: "#E32424" },
  league_admin: { label: "League Admin", icon: ShieldIcon, color: "#00A86B" },
  court_umpire: { label: "Court Umpire", icon: Gavel,      color: "#F4B400" },
  owner:        { label: "Team Owner",   icon: Users2,     color: "#7DD3FC" },
  spectator:    { label: "Spectator",    icon: Users2,     color: "#9CA3AF" },
};

const STATUS_FILTERS = [
  { key: "all",       label: "All",       dot: "bg-zinc-400" },
  { key: "pending",   label: "Pending",   dot: "bg-amber-500 animate-pulse" },
  { key: "active",    label: "Active",    dot: "bg-[#00A86B]" },
  { key: "suspended", label: "Suspended", dot: "bg-red-500" },
];

export default function UsersManagement() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [resetUser, setResetUser] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    return users.filter((u) => {
      if (statusFilter !== "all" && (u.status || "active") !== statusFilter) return false;
      if (roleFilter !== "all" && u.role !== roleFilter) return false;
      return true;
    });
  }, [users, statusFilter, roleFilter]);

  const counts = useMemo(() => ({
    all: users.length,
    pending:   users.filter((u) => u.status === "pending").length,
    active:    users.filter((u) => (u.status || "active") === "active").length,
    suspended: users.filter((u) => u.status === "suspended").length,
  }), [users]);

  const onApprove = async (u) => {
    try { await api.post(`/users/${u.id}/approve`); toast.success(`Approved ${u.email}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const onSuspend = async (u) => {
    if (!window.confirm(`Suspend ${u.email}? They won't be able to sign in.`)) return;
    try { await api.post(`/users/${u.id}/suspend`); toast.success(`Suspended ${u.email}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const onUnsuspend = async (u) => {
    try { await api.post(`/users/${u.id}/unsuspend`); toast.success(`Reactivated ${u.email}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const onDelete = async (u) => {
    if (!window.confirm(`Permanently delete ${u.email}? This cannot be undone.`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success(`Deleted ${u.email}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl">
      <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Role-Based Access Control</div>
          <h1 className="font-anton text-5xl md:text-6xl uppercase tracking-tighter text-white" data-testid="users-page-title">User Management</h1>
          <div className="text-xs text-zinc-400 mt-1">
            {users.length} users · {counts.pending} pending · {counts.active} active · {counts.suspended} suspended
          </div>
        </div>
        <CreateUserDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={load} />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6" data-testid="users-filters">
        <div className="flex flex-wrap gap-2">
          {STATUS_FILTERS.map((s) => (
            <button
              key={s.key}
              data-testid={`users-status-filter-${s.key}`}
              onClick={() => setStatusFilter(s.key)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs uppercase tracking-widest font-bold transition-colors ${statusFilter === s.key ? "border-white/30 bg-white/10 text-white" : "border-white/10 bg-[#11241b] text-zinc-400 hover:text-white"}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
              {s.label}
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-black/30 text-zinc-300">{counts[s.key]}</span>
            </button>
          ))}
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-48 bg-[#11241b] border-white/10 text-white" data-testid="users-role-filter">
            <SelectValue placeholder="All roles" />
          </SelectTrigger>
          <SelectContent className="bg-[#11241b] border-white/10 text-white">
            <SelectItem value="all">All roles</SelectItem>
            {Object.entries(ROLE_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card className="bg-[#11241b] border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/10">
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Joined</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={5} className="px-4 py-10 text-center text-zinc-500">Loading…</td></tr>}
              {!loading && filtered.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-zinc-500" data-testid="users-empty">No users match this filter.</td></tr>}
              {!loading && filtered.map((u) => {
                const meta = ROLE_META[u.role] || { label: u.role, color: "#9CA3AF", icon: Users2 };
                const RoleIcon = meta.icon;
                const status = u.status || "active";
                const isMe = me?.id === u.id;
                return (
                  <tr key={u.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`user-row-${u.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-bold text-white" data-testid={`user-name-${u.id}`}>{u.name || "—"} {isMe && <span className="text-[9px] uppercase tracking-widest text-[#00A86B] ml-1">YOU</span>}</div>
                      <div className="text-xs text-zinc-400" data-testid={`user-email-${u.id}`}>{u.email}</div>
                      {u.signup_note && <div className="text-[11px] text-zinc-500 mt-1 italic max-w-xs truncate" title={u.signup_note}>&quot;{u.signup_note}&quot;</div>}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] uppercase tracking-widest font-bold" style={{ background: `${meta.color}22`, color: meta.color, border: `1px solid ${meta.color}55` }} data-testid={`user-role-${u.id}`}>
                        <RoleIcon className="w-3 h-3" /> {meta.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={status} testid={`user-status-${u.id}`} />
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-400">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 justify-end flex-wrap">
                        {status === "pending" && (
                          <Button size="sm" onClick={() => onApprove(u)} className="bg-[#00A86B] hover:bg-[#00935A] h-8 text-xs" data-testid={`approve-user-${u.id}`}>
                            <Check className="w-3 h-3 mr-1" /> Approve
                          </Button>
                        )}
                        {status === "active" && !isMe && (
                          <Button size="sm" variant="outline" onClick={() => onSuspend(u)} className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10 h-8 text-xs" data-testid={`suspend-user-${u.id}`}>
                            <Ban className="w-3 h-3 mr-1" /> Suspend
                          </Button>
                        )}
                        {status === "suspended" && (
                          <Button size="sm" variant="outline" onClick={() => onUnsuspend(u)} className="border-[#00A86B]/40 text-[#00A86B] hover:bg-[#00A86B]/10 h-8 text-xs" data-testid={`unsuspend-user-${u.id}`}>
                            <RefreshCw className="w-3 h-3 mr-1" /> Reactivate
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => setEditUser(u)} className="border-white/10 text-zinc-300 hover:bg-white/5 h-8 text-xs" data-testid={`edit-user-${u.id}`}>
                          Edit
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setResetUser(u)} className="border-white/10 text-zinc-300 hover:bg-white/5 h-8 text-xs" data-testid={`reset-user-${u.id}`}>
                          <KeyRound className="w-3 h-3" />
                        </Button>
                        {!isMe && (
                          <Button size="sm" variant="ghost" onClick={() => onDelete(u)} className="text-red-400 hover:bg-red-500/10 h-8 px-2" data-testid={`delete-user-${u.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <EditUserDialog key={`edit-${editUser?.id || "none"}`} user={editUser} onClose={() => setEditUser(null)} onSaved={load} />
      <ResetPasswordDialog key={`reset-${resetUser?.id || "none"}`} user={resetUser} onClose={() => setResetUser(null)} />
    </div>
  );
}

/* ============================================================
   Sub-components
   ============================================================ */
function StatusBadge({ status, testid }) {
  const cfg = {
    pending:   { label: "Pending",   color: "#F59E0B" },
    active:    { label: "Active",    color: "#00A86B" },
    suspended: { label: "Suspended", color: "#EF4444" },
  }[status] || { label: status, color: "#9CA3AF" };
  return (
    <span data-testid={testid} className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] uppercase tracking-widest font-bold" style={{ background: `${cfg.color}22`, color: cfg.color, border: `1px solid ${cfg.color}55` }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: cfg.color }} />
      {cfg.label}
    </span>
  );
}

function CreateUserDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "owner", team_id: "" });
  const [submitting, setSubmitting] = useState(false);
  const [teams, setTeams] = useState([]);

  useEffect(() => {
    if (!open) return;
    api.get("/teams").then(r => setTeams(r.data || [])).catch(() => {});
  }, [open]);

  const submit = async () => {
    if (!form.name || !form.email || !form.password) return toast.error("All fields are required");
    if (form.password.length < 6) return toast.error("Password must be at least 6 characters");
    if (form.role === "owner" && !form.team_id) return toast.error("Team owners must be linked to a team");
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.team_id) delete payload.team_id;
      await api.post("/users", payload);
      toast.success(`Created ${form.email}`);
      setForm({ name: "", email: "", password: "", role: "owner", team_id: "" });
      onOpenChange(false);
      onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button data-testid="open-create-user" className="bg-[#E32424] hover:bg-[#a01818] text-white font-bold uppercase tracking-widest h-11">
          <UserPlus className="w-4 h-4 mr-2" /> Create User
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#11241b] border-white/10 text-white" data-testid="create-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-anton text-2xl uppercase tracking-tight">Create User</DialogTitle>
          <DialogDescription className="text-zinc-400">Direct admin-create — user is active immediately and can sign in with this password.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">Name</Label>
            <Input data-testid="create-user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#060d09] border-white/10 text-white mt-1" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">Email</Label>
            <Input data-testid="create-user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#060d09] border-white/10 text-white mt-1" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">Password</Label>
            <Input data-testid="create-user-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="min 6 characters" className="bg-[#060d09] border-white/10 text-white mt-1" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v, team_id: v === "owner" ? form.team_id : "" })}>
              <SelectTrigger data-testid="create-user-role" className="bg-[#060d09] border-white/10 text-white mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#11241b] border-white/10 text-white">
                {Object.entries(ROLE_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {form.role === "owner" && (
            <div data-testid="create-user-team-block">
              <Label className="text-xs uppercase tracking-widest text-[#F4B400]">Link to Team *</Label>
              <Select value={form.team_id} onValueChange={(v) => setForm({ ...form, team_id: v })}>
                <SelectTrigger data-testid="create-user-team" className="bg-[#060d09] border-[#F4B400]/40 text-white mt-1">
                  <SelectValue placeholder="— pick the team this owner manages —" />
                </SelectTrigger>
                <SelectContent className="bg-[#11241b] border-white/10 text-white max-h-64">
                  {teams.map(t => <SelectItem key={t.id} value={t.id}>{t.name}{t.group ? ` · Group ${t.group}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="text-[10px] text-zinc-500 mt-1">Owners can only see their linked team&apos;s kitty, roster &amp; lineup.</div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button data-testid="submit-create-user" disabled={submitting} onClick={submit} className="bg-[#00A86B] hover:bg-[#00935A] text-white font-bold uppercase tracking-widest">
            {submitting ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditUserDialog({ user, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({ name: user?.name || "", role: user?.role || "owner", team_id: user?.team_id || "" }));
  const [submitting, setSubmitting] = useState(false);
  const [teams, setTeams] = useState([]);

  useEffect(() => {
    if (!user) return;
    api.get("/teams").then(r => setTeams(r.data || [])).catch(() => {});
    setForm({ name: user.name || "", role: user.role || "owner", team_id: user.team_id || "" });
  }, [user]);

  if (!user) return null;

  const submit = async () => {
    if (form.role === "owner" && !form.team_id) return toast.error("Team owners must be linked to a team");
    setSubmitting(true);
    try {
      const payload = { ...form };
      // Send explicit null if role is non-owner but we're clearing team assignment
      if (form.role !== "owner") payload.team_id = null;
      await api.patch(`/users/${user.id}`, payload);
      toast.success(`Updated ${user.email}`);
      onSaved();
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    setSubmitting(false);
  };

  return (
    <Dialog open={!!user} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#11241b] border-white/10 text-white" data-testid="edit-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-anton text-2xl uppercase tracking-tight">Edit User</DialogTitle>
          <DialogDescription className="text-zinc-400">{user.email}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">Name</Label>
            <Input data-testid="edit-user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#060d09] border-white/10 text-white mt-1" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v, team_id: v === "owner" ? form.team_id : "" })}>
              <SelectTrigger data-testid="edit-user-role" className="bg-[#060d09] border-white/10 text-white mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#11241b] border-white/10 text-white">
                {Object.entries(ROLE_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {form.role === "owner" && (
            <div data-testid="edit-user-team-block">
              <Label className="text-xs uppercase tracking-widest text-[#F4B400]">Link to Team *</Label>
              <Select value={form.team_id} onValueChange={(v) => setForm({ ...form, team_id: v })}>
                <SelectTrigger data-testid="edit-user-team" className="bg-[#060d09] border-[#F4B400]/40 text-white mt-1">
                  <SelectValue placeholder="— pick a team —" />
                </SelectTrigger>
                <SelectContent className="bg-[#11241b] border-white/10 text-white max-h-64">
                  {teams.map(t => <SelectItem key={t.id} value={t.id}>{t.name}{t.group ? ` · Group ${t.group}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="text-[10px] text-zinc-500 mt-1">Fixes empty Command Center / Team Profile for owners who couldn&apos;t see their team data.</div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button data-testid="submit-edit-user" disabled={submitting} onClick={submit} className="bg-[#00A86B] hover:bg-[#00935A] text-white font-bold uppercase tracking-widest">
            {submitting ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ user, onClose }) {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!user) return null;

  const submit = async () => {
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    setSubmitting(true);
    try {
      await api.post(`/users/${user.id}/reset-password`, { password });
      toast.success(`Reset password for ${user.email}`);
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    setSubmitting(false);
  };

  return (
    <Dialog open={!!user} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#11241b] border-white/10 text-white" data-testid="reset-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-anton text-2xl uppercase tracking-tight">Reset Password</DialogTitle>
          <DialogDescription className="text-zinc-400">Setting a new password for {user.email}. Share it securely.</DialogDescription>
        </DialogHeader>
        <div>
          <Label className="text-xs uppercase tracking-widest text-zinc-400">New password</Label>
          <Input data-testid="reset-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="min 6 characters" className="bg-[#060d09] border-white/10 text-white mt-1" />
        </div>
        <DialogFooter>
          <Button data-testid="submit-reset-password" disabled={submitting} onClick={submit} className="bg-[#E32424] hover:bg-[#a01818] text-white font-bold uppercase tracking-widest">
            {submitting ? "Resetting…" : "Set New Password"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
